/**
 * The GeoPackage → GeoJSON step, free of DOM and React so it runs the same in a worker and in
 * the thread. Errors leave as plain `{ code, message }` so they survive postMessage.
 */
import type { Feature, FeatureCollection, Geometry, Position } from 'geojson';
import { isKnownCrs, toWgs84 } from './crs';
import type { Database } from 'sql.js';
import type { VectorProgress } from './vectorSource';
import { decodeGeometry, encodeGeometry, featureTables, GpkgError, openGpkg, type FeatureTable, type GpkgErrorCode } from './gpkg';
import { METRES_PER_DEGREE, simplifyGeometry } from './simplify';

export type ParseErrorCode = 'unsupported_crs' | GpkgErrorCode;

export class ParseError extends Error {
  constructor(
    public readonly code: ParseErrorCode,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'ParseError';
  }
}

function reproject(geometry: Geometry, crs: string): Geometry {
  const at = (p: Position): Position => toWgs84(crs, [p[0]!, p[1]!]);
  switch (geometry.type) {
    case 'Point':
      return { ...geometry, coordinates: at(geometry.coordinates) };
    case 'MultiPoint':
    case 'LineString':
      return { ...geometry, coordinates: geometry.coordinates.map(at) };
    case 'MultiLineString':
    case 'Polygon':
      return { ...geometry, coordinates: geometry.coordinates.map((line) => line.map(at)) };
    case 'MultiPolygon':
      return { ...geometry, coordinates: geometry.coordinates.map((poly) => poly.map((line) => line.map(at))) };
    case 'GeometryCollection':
      return { ...geometry, geometries: geometry.geometries.map((g) => reproject(g, crs)) };
  }
}

export type Layer = FeatureCollection<Geometry, Record<string, unknown>>;

export interface Parsed {
  fc: Layer;
  /**
   * The same GeoPackage with the table rewritten in EPSG:4326, when it was in another system:
   * cached in place of the original so the next read skips the reprojection. Null otherwise.
   */
  normalized: Uint8Array | null;
}

/** Read one feature table into GeoJSON; `onFeatures` fires as soon as the features exist, before any rewrite. */
/**
 * The table's geometries replaced by their EPSG:4326 versions and the srs bookkeeping moved
 * along. GDAL's R-tree triggers call SpatiaLite functions sql.js does not have, so the
 * triggers and the index go first; this copy is only ever read by our own parser.
 */
function rewriteInWgs84(db: Database, table: string, column: string, geometries: [number, Geometry][]): Uint8Array {
  const q = (name: string) => `"${name.replace(/"/g, '""')}"`;
  const triggers = db.exec(`SELECT name FROM sqlite_master WHERE type = 'trigger' AND tbl_name = ?`, [table])[0]?.values ?? [];
  for (const [name] of triggers) db.run(`DROP TRIGGER IF EXISTS ${q(String(name))}`);
  try {
    db.run(`DROP TABLE IF EXISTS ${q(`rtree_${table}_${column}`)}`);
  } catch {
    // sql.js is built without the rtree module and cannot even drop its virtual table; left
    // stale, it is never consulted: this copy is only read by our own parser.
  }
  if (db.exec(`SELECT 1 FROM sqlite_master WHERE type = 'table' AND name = 'gpkg_extensions'`).length) {
    db.run(`DELETE FROM gpkg_extensions WHERE table_name = ? AND extension_name = 'gpkg_rtree_index'`, [table]);
  }
  const update = db.prepare(`UPDATE ${q(table)} SET ${q(column)} = ? WHERE rowid = ?`);
  try {
    for (const [rowid, geometry] of geometries) update.run([encodeGeometry(geometry, 4326), rowid]);
  } finally {
    update.free();
  }
  db.run(
    `INSERT OR IGNORE INTO gpkg_spatial_ref_sys (srs_name, srs_id, organization, organization_coordsys_id, definition)
     VALUES ('WGS 84', 4326, 'EPSG', 4326, 'GEOGCS["WGS 84",DATUM["WGS_1984",SPHEROID["WGS 84",6378137,298.257223563]],PRIMEM["Greenwich",0],UNIT["degree",0.0174532925199433]]')`,
  );
  db.run(`UPDATE gpkg_contents SET srs_id = 4326 WHERE table_name = ?`, [table]);
  db.run(`UPDATE gpkg_geometry_columns SET srs_id = 4326 WHERE table_name = ?`, [table]);
  return db.export();
}

/** One feature table's rows as features, reprojected to WGS84; the rewritten geometries too, for the cache. */
function readTable(db: Database, chosen: FeatureTable, onProgress?: (p: VectorProgress) => void, simplifyM?: number): { features: Feature<Geometry, Record<string, unknown>>[]; rewritten: [number, Geometry][] } {
  const { crs } = chosen;
  if (crs !== 'EPSG:4326' && (crs === null || !isKnownCrs(crs))) {
    throw new ParseError('unsupported_crs', crs ?? 'unknown srs');
  }
  const quoted = `"${chosen.table.replace(/"/g, '""')}"`;
  onProgress?.({ phase: 'reading', step: 'rows' });
  const result = db.exec(`SELECT rowid AS __rowid, * FROM ${quoted} ORDER BY rowid`)[0];
  const names = (result?.columns ?? []).slice(1);
  const rows = (result?.values ?? []).map((r) => r.slice(1));
  const rowids = (result?.values ?? []).map((r) => r[0] as number);
  const geomIndex = names.indexOf(chosen.geometryColumn);
  const fidIndex = names.indexOf('fid');
  const features: Feature<Geometry, Record<string, unknown>>[] = [];
  const rewritten: [number, Geometry][] = [];
  onProgress?.({ phase: 'reading', step: 'features', done: 0, total: rows.length });
  rows.forEach((row, r) => {
    if (r > 0 && r % PROGRESS_EVERY === 0) onProgress?.({ phase: 'reading', step: 'features', done: r, total: rows.length });
    const decoded = decodeGeometry((row[geomIndex] as Uint8Array | null) ?? new Uint8Array());
    if (!decoded) return;
    const simple = simplifyM ? simplifyGeometry(decoded, crs === 'EPSG:4326' ? simplifyM / METRES_PER_DEGREE : simplifyM) : decoded;
    const geometry = crs === 'EPSG:4326' ? simple : reproject(simple, crs);
    if (crs !== 'EPSG:4326') rewritten.push([rowids[r]!, geometry]);
    const properties: Record<string, unknown> = {};
    names.forEach((n, i) => {
      if (i !== geomIndex && i !== fidIndex) properties[n] = row[i] ?? null;
    });
    features.push({ type: 'Feature', id: fidIndex >= 0 ? (row[fidIndex] as number) : features.length + 1, geometry, properties });
  });
  return { features, rewritten };
}

/**
 * Several tables of one file in one pass over its bytes: one database, each table read as
 * `parseGpkgLayer` reads one. A table that is not there is left out; nothing is offered for the
 * cache. `simplifyM` is per table.
 */
export async function parseGpkgTables(bytes: Uint8Array, tables: string[], onProgress?: (p: VectorProgress) => void, simplifyM: Partial<Record<string, number>> = {}): Promise<Record<string, Layer>> {
  let db;
  onProgress?.({ phase: 'reading', step: 'open' });
  try {
    db = await openGpkg(bytes);
  } catch (e) {
    if (e instanceof GpkgError) throw new ParseError(e.code, e.message);
    throw e;
  }
  try {
    const known = featureTables(db);
    const out: Record<string, Layer> = {};
    for (const table of tables) {
      const chosen = known.find((t) => t.table === table);
      if (!chosen) continue;
      try {
        out[table] = { type: 'FeatureCollection', features: readTable(db, chosen, onProgress, simplifyM[table]).features };
      } catch (e) {
        if (!(e instanceof ParseError)) throw e;
        // A table in a system the viewer cannot draw is left out like a missing one.
      }
    }
    return out;
  } finally {
    db.close();
  }
}

const PROGRESS_EVERY = 2000; // rows between two progress reports: often enough to move, rare enough to be free

/** `onFeatures` fires as soon as the features exist, before any rewrite; `onProgress` names the step under way. */
/**
 * `simplifyM`, when given, simplifies polygon outlines to that many metres (Douglas–Peucker), in
 * the table's own projected system before reprojection, or its equivalent in degrees for a
 * geographic table. Such a read is not a faithful copy, so it offers no rewrite for the cache.
 */
export async function parseGpkgLayer(bytes: Uint8Array, table?: string, onFeatures?: (fc: Layer) => void, onProgress?: (p: VectorProgress) => void, simplifyM?: number): Promise<Parsed> {
  let db;
  onProgress?.({ phase: 'reading', step: 'open' });
  try {
    db = await openGpkg(bytes);
  } catch (e) {
    if (e instanceof GpkgError) throw new ParseError(e.code, e.message);
    throw e;
  }
  try {
    const tables = featureTables(db);
    const chosen = table ? tables.find((t) => t.table === table) : tables[0];
    if (!chosen) throw new ParseError('no_feature_table');
    const { features, rewritten } = readTable(db, chosen, onProgress, simplifyM);
    const fc: Layer = { type: 'FeatureCollection', features };
    onFeatures?.(fc);
    if (rewritten.length === 0 || simplifyM) return { fc, normalized: null };
    try {
      return { fc, normalized: rewriteInWgs84(db, chosen.table, chosen.geometryColumn, rewritten) };
    } catch {
      return { fc, normalized: null }; // a copy we cannot make costs nothing: the original stays cached
    }
  } catch (e) {
    if (e instanceof GpkgError) throw new ParseError(e.code, e.message);
    throw e;
  } finally {
    db.close();
  }
}
